import { store } from '../../store'
import { setAuthenticatedAction, setErrorAction } from '../../providers/Authentication/store/actions'

// Refresh this far ahead of the token's stated expiry so we never present an
// already-dead token on a (re)connect or request.
const REFRESH_SKEW_MS = 60_000

const authApiUrl = () => store.getState().hostDns.authApi

const readToken = () => {
    const raw = sessionStorage.getItem('authToken')
    if (!raw) return null
    try {
        return JSON.parse(raw)
    } catch {
        return null
    }
}

const isExpiringSoon = (tokenData) =>
    !tokenData || new Date(tokenData.Expires) < new Date(Date.now() + REFRESH_SKEW_MS)

const setUnauthenticated = () =>
    store.dispatch(setAuthenticatedAction({ isAuthenticated: false, departments: [], scopes: [], userName: '' }))

// A single in-flight refresh shared by every caller. The server rotates the
// refresh token on each /refresh, so two concurrent refreshes would make the
// second present an already-consumed token and force a spurious logout.
let inFlightRefresh = null

const doRefresh = async () => {
    const refreshToken = sessionStorage.getItem('refreshToken')
    if (!refreshToken) {
        store.dispatch(setErrorAction({ message: 'No refresh token found.', error: 'refreshToken not found in session storage' }))
        return null
    }

    let response
    try {
        response = await fetch(`${authApiUrl()}/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(refreshToken)
        })
    } catch (err) {
        // Transient network failure (e.g. just woke from sleep). Keep the
        // existing token and let the caller / next schedule retry.
        store.dispatch(setErrorAction({ message: 'Error refreshing token.', error: err?.message ?? String(err) }))
        return null
    }

    if (response.ok) {
        const data = await response.json()
        sessionStorage.setItem('authToken', data.accessToken)
        sessionStorage.setItem('refreshToken', data.refreshToken)
        const tokenData = JSON.parse(data.accessToken)
        store.dispatch(setAuthenticatedAction({
            isAuthenticated: true,
            departments: tokenData.Departments,
            scopes: tokenData.Scopes,
            userName: tokenData.UserName
        }))
        return tokenData
    }

    if (response.status === 401) {
        sessionStorage.removeItem('authToken')
        sessionStorage.removeItem('refreshToken')
        setUnauthenticated()
        store.dispatch(setErrorAction({ message: 'Session expired.', error: 'Refresh token expired or invalid.' }))
        window.location.replace(authApiUrl())
        return null
    }

    store.dispatch(setErrorAction({ message: 'Error refreshing token.', error: response.statusText }))
    return null
}

// Refresh the access token, coalescing concurrent callers onto one request.
export const refreshAccessToken = () => {
    if (inFlightRefresh) return inFlightRefresh
    inFlightRefresh = doRefresh().finally(() => { inFlightRefresh = null })
    return inFlightRefresh
}

// Returns a valid encrypted bearer token, refreshing first if it is expired or
// about to expire. Returns null when there is no usable session. SignalR's
// accessTokenFactory and authFetch both call this so every (re)connect and
// request carries a fresh token, even after the tab was suspended.
export const getValidAccessToken = async () => {
    let tokenData = readToken()
    if (!tokenData) return null
    if (isExpiringSoon(tokenData)) {
        const refreshed = await refreshAccessToken()
        tokenData = refreshed ?? readToken()
    }
    return tokenData?.EncryptedToken ?? null
}

// Self-rescheduling refresh loop, driven by the token's actual expiry. Idempotent:
// each call clears any prior timer, so re-renders cannot stack duplicate timers
// (which previously caused overlapping refreshes and refresh-token rotation races).
let refreshTimer = null

const scheduleNextRefresh = () => {
    if (refreshTimer) clearTimeout(refreshTimer)
    refreshTimer = null

    const tokenData = readToken()
    if (!tokenData) return

    const delay = Math.max(0, new Date(tokenData.Expires) - Date.now() - REFRESH_SKEW_MS)
    refreshTimer = setTimeout(async () => {
        await refreshAccessToken()
        scheduleNextRefresh()
    }, delay)
}

export const startTokenRefreshLoop = () => scheduleNextRefresh()
